import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, Clock, Mail, Phone, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw, Search, Filter, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'agreenersideapplegarth';

const statusColors = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  confirmed: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' }
};

const visitTypeLabels = {
  'guided-tour': 'Guided Tour',
  'workshop': 'Permaculture Workshop',
  'day-pass': 'Full Day Experience',
  'private-session': 'Private Study Session'
};

// Password Protection Component
const PasswordGate = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('adminAuth', 'true');
      onSuccess();
    } else {
      setError(true);
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto bg-black rounded-full p-4 w-16 h-16 flex items-center justify-center mb-4">
            <Lock className="text-white" size={28} />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: "'Crimson Text', serif" }}>
            Admin Access
          </CardTitle>
          <CardDescription>
            Enter password to access the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                placeholder="Enter password"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                  error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                autoFocus
              />
              {error && (
                <p className="text-red-500 text-sm mt-2">Incorrect password. Please try again.</p>
              )}
            </div>
            <Button type="submit" className="w-full bg-black hover:bg-gray-800 text-white py-3">
              Access Dashboard
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/bookings`);
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      const data = await response.json();
      // Sort by date, most recent first
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
    }
  }, [isAuthenticated]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    setUpdatingId(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      const updatedBooking = await response.json();
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? updatedBooking : b)
      );
      toast.success(`Booking ${newStatus}!`);
    } catch (err) {
      toast.error('Failed to update booking status');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      setBookings(prev => prev.filter(b => b.id !== bookingId));
      toast.success('Booking deleted');
    } catch (err) {
      toast.error('Failed to delete booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.phone && booking.phone.includes(searchTerm));
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (isoString) => {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
  };

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-16">
      {/* Header */}
      <div className="bg-black text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white hover:text-emerald-400 hover:bg-white/10"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Home
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white/50 text-white hover:bg-white hover:text-black"
            >
              Logout
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 
                className="text-3xl sm:text-4xl font-bold"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Admin Dashboard
              </h1>
              <p className="text-gray-300 mt-2">Manage bookings and appointments</p>
            </div>
            <Button
              onClick={fetchBookings}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-black"
              disabled={loading}
            >
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Bookings</p>
                  <p className="text-3xl font-bold text-black">{stats.total}</p>
                </div>
                <Calendar className="text-gray-400" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-3xl font-bold text-yellow-800">{stats.pending}</p>
                </div>
                <AlertCircle className="text-yellow-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Confirmed</p>
                  <p className="text-3xl font-bold text-green-800">{stats.confirmed}</p>
                </div>
                <CheckCircle className="text-green-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Completed</p>
                  <p className="text-3xl font-bold text-blue-800">{stats.completed}</p>
                </div>
                <CheckCircle className="text-blue-500" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-600" size={48} />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="mx-auto text-red-500 mb-2" size={48} />
            <p className="text-red-700">{error}</p>
            <Button onClick={fetchBookings} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredBookings.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Calendar className="mx-auto text-gray-300 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Bookings Found</h3>
            <p className="text-gray-500">
              {bookings.length === 0 
                ? "No bookings have been made yet."
                : "No bookings match your search criteria."}
            </p>
          </div>
        )}

        {/* Bookings List */}
        {!loading && !error && filteredBookings.length > 0 && (
          <div className="space-y-4">
            {filteredBookings.map((booking) => {
              const statusStyle = statusColors[booking.status] || statusColors.pending;
              
              return (
                <Card 
                  key={booking.id} 
                  className={`bg-white hover:shadow-md transition-shadow ${statusStyle.border} border-l-4`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Main Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-black">{booking.name}</h3>
                          <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail size={16} />
                            <a href={`mailto:${booking.email}`} className="hover:text-emerald-600">
                              {booking.email}
                            </a>
                          </div>
                          {booking.phone && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone size={16} />
                              <a href={`tel:${booking.phone}`} className="hover:text-emerald-600">
                                {booking.phone}
                              </a>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar size={16} />
                            <span>{formatDate(booking.date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock size={16} />
                            <span>{booking.time_slot}</span>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <span className="text-sm bg-gray-100 px-3 py-1 rounded-full">
                            {visitTypeLabels[booking.visit_type] || booking.visit_type}
                          </span>
                          <span className="text-sm bg-gray-100 px-3 py-1 rounded-full flex items-center gap-1">
                            <Users size={14} />
                            {booking.group_size} guest{booking.group_size > 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-gray-400">
                            Booked: {formatDateTime(booking.created_at)}
                          </span>
                        </div>

                        {booking.special_requests && (
                          <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                            <strong>Special Requests:</strong> {booking.special_requests}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 lg:flex-col">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              disabled={updatingId === booking.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {updatingId === booking.id ? (
                                <Loader2 className="animate-spin mr-1" size={14} />
                              ) : (
                                <CheckCircle className="mr-1" size={14} />
                              )}
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              disabled={updatingId === booking.id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="mr-1" size={14} />
                              Cancel
                            </Button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <Button
                            size="sm"
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            disabled={updatingId === booking.id}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {updatingId === booking.id ? (
                              <Loader2 className="animate-spin mr-1" size={14} />
                            ) : (
                              <CheckCircle className="mr-1" size={14} />
                            )}
                            Mark Complete
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteBooking(booking.id)}
                          className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
